import DocumentSystemApi from '@/api/documentSystem'
import PptxConvertApi from '@/api/pptxConvert'

const DEFAULT_POLL_INTERVAL_MS = 2000
const DEFAULT_MAX_ATTEMPTS = 600

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms) })

// Signals that the status GET failed with a transient TRANSPORT error (offline)
// and the caller should retry rather than treat it as a terminal failure.
const RETRY = Symbol('poll-retry')

// A thrown status GET is a transport/network error (retryable) ONLY when it
// carries no HTTP status. The same-origin status GETs go through
// `DocumentSystemApi` (AHA-40764), which is backed by vue-resource: on a
// non-2xx response it rejects with a Response whose `.status` is the HTTP code
// (4xx/5xx), and on a genuine transport failure (offline, DNS, CORS, timeout,
// abort) it rejects with a Response whose `.status` is 0. So a real HTTP error
// status is a hard failure and must NOT be masked as "connection lost", or a
// persistently-broken endpoint (500/401) would retry forever behind the amber
// "Waiting to reconnect…" with no error card ever shown.
const isTransportError = (error) => !error || !error.status

// One status GET via the injected same-origin `fetchStatus` callback, which
// resolves with the response BODY directly. On success reports connectivity
// restored and returns the body. On a TRANSPORT error, when resilient
// (onConnectionChange supplied, AHA2-171), reports the outage and asks the loop
// to retry so the import can auto-resume when the network returns; otherwise
// (flag-off, or an HTTP error status) rethrows so the caller surfaces a real
// failure.
const fetchStatusOnce = async ({ fetchStatus, resilient, markConnection }) => {
  try {
    const body = await fetchStatus()
    markConnection(false)
    return body
  } catch (error) {
    if (!resilient || !isTransportError(error)) throw error
    markConnection(true)
    return RETRY
  }
}

const pollLoop = async ({
  label,
  fetchStatus,
  isCancelled,
  pollIntervalMs,
  maxAttempts,
  parse,
  onConnectionChange,
}) => {
  const resilient = typeof onConnectionChange === 'function'
  const cancelled = () => isCancelled?.() === true
  let connectionLost = false
  const markConnection = (lost) => {
    if (!resilient || lost === connectionLost) return
    connectionLost = lost
    onConnectionChange(lost)
  }

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    // Cancel check BEFORE the inter-poll wait — so a cancel that lands while we
    // were sleeping (or on a retry tick) is honoured immediately, not up to one
    // interval later.
    if (cancelled()) return null
    if (attempt > 0) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(pollIntervalMs)
    }
    // Second cancel check after the wait, so a cancel during the sleep is
    // honoured before the next GET fires.
    if (cancelled()) return null

    // eslint-disable-next-line no-await-in-loop
    const body = await fetchStatusOnce({ fetchStatus, resilient, markConnection })
    // Transport error (offline): retry, but DO count the attempt — the timeout
    // stays live so a permanently-dead network trips maxAttempts (~20 min) and
    // throws instead of hanging forever in "Waiting to reconnect…". A network
    // that returns within that window auto-resumes (§5). The next tick's
    // top-of-loop sleep provides the single inter-poll wait (no double sleep).
    if (body === RETRY) {
      // eslint-disable-next-line no-continue
      continue
    }
    if (cancelled()) return null
    const outcome = parse(body)
    if (outcome) return outcome
  }
  const timeoutSeconds = (maxAttempts * pollIntervalMs) / 1000
  throw new Error(`Polling ${label} timed out after ${timeoutSeconds}s.`)
}

/**
 * Resolve once the cloud-imported file's background download has finished.
 * Throws on failure or timeout. `onMeta` (optional) fires on each tick with
 * the file metadata so the caller can mirror progress into local state.
 */
export const waitForCloudImportReady = async (
  hashId,
  {
    isCancelled,
    onMeta,
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
  } = {},
) => {
  return pollLoop({
    label: `cloud import ${hashId}`,
    fetchStatus: () => DocumentSystemApi.getCloudImportStatus(hashId),
    isCancelled,
    pollIntervalMs,
    maxAttempts,
    parse: (body) => {
      const meta = body?.metadata
      onMeta?.(meta)
      const status = meta?.import_status
      if (status === 'success') {
        return { meta }
      }
      if (status === 'failed') {
        throw new Error(meta?.import_error || 'Cloud import failed.')
      }
      return null
    },
  })
}

/**
 * AHA-47757: resolve once a PPTX → content-v2 conversion job (the
 * document-system converter, see @/api/pptxConvert) reaches a terminal state. Throws on failure or timeout; returns the final
 * job body on success. Same loop semantics as waitForImportJob — cancel,
 * transient-transport retry and connection reporting all behave identically —
 * only the endpoint and the status vocabulary differ (done/failed vs
 * success/failed, and progress lives in `slides[]` rather than `result`).
 */
export const waitForPptxConvertJob = async (
  jobId,
  {
    isCancelled,
    onProgress,
    onConnectionChange,
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
  } = {},
) => {
  return pollLoop({
    label: `pptx convert job ${jobId}`,
    fetchStatus: () => PptxConvertApi.getConvertJobStatus(jobId),
    isCancelled,
    pollIntervalMs,
    maxAttempts,
    onConnectionChange,
    parse: (body) => {
      const status = body?.status
      onProgress?.(body)
      if (status === 'done') {
        return body
      }
      if (status === 'failed') {
        // `error` is the converter's coded dict {code, message} (never a
        // string) — this message is what Sentry and Mixpanel record, so it
        // must not read "[object Object]". The code and the per-slide list
        // ride along for callers that want to say what did land.
        const detail = body?.error
        const msg = detail?.message || detail?.code || 'Unknown error'
        const error = new Error(`PPTX convert job ${jobId} failed: ${msg}`)
        error.code = detail?.code
        error.slides = body?.slides
        throw error
      }
      return null
    },
  })
}

/**
 * Resolve once an import job reaches a terminal state. Throws on failure or
 * timeout. Returns the final job body on success.
 */
export const waitForImportJob = async (
  jobId,
  {
    isCancelled,
    onProgress,
    onConnectionChange,
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
  } = {},
) => {
  return pollLoop({
    label: `import job ${jobId}`,
    fetchStatus: () => DocumentSystemApi.getImportJobStatus(jobId),
    isCancelled,
    pollIntervalMs,
    maxAttempts,
    onConnectionChange,
    parse: (body) => {
      const status = body?.status
      // `result` carries the import's sub-status while `status` is still
      // 'processing': phase, slide counts and caption counts. It is how the
      // caller learns the deck is usable (result.slides_ready) long before the
      // job reaches a terminal state — under the v2 pipeline captioning runs
      // after the slides exist, so 'success' lands well after they are visible.
      //
      // Deliberately not a new `status` value: this loop resolves on 'success',
      // throws on 'failed' and returns null for anything else, so an unknown
      // status would poll to maxAttempts and then throw on a healthy import.
      onProgress?.(body?.result, body)
      if (status === 'success') {
        return body
      }
      if (status === 'failed') {
        const msg = body?.error_message || 'Unknown error'
        throw new Error(`Import job ${jobId} failed: ${msg}`)
      }
      return null
    },
  })
}
